import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMediaSettings } from '../../contexts/MediaSettingsContext';
import './MediaSettingsModal.css';

const TABS = [
  { id: 'microphone', label: 'Microphone', icon: '🎤' },
  { id: 'speaker', label: 'Speaker', icon: '🔊' },
  { id: 'camera', label: 'Camera', icon: '📷' },
  { id: 'background', label: 'Background', icon: '🖼️' },
  { id: 'advanced', label: 'Advanced', icon: '⚙️' },
];

export default function MediaSettingsModal() {
  const {
    settings,
    saveSettings,
    devices,
    refreshDevices,
    presets,
    userAssets,
    uploadBackgroundImage,
    deleteBackgroundImage,
    getAssetUrl,
    isModalOpen,
    closeMediaSettings,
    activeTab,
    setActiveTab,
    loading,
  } = useMediaSettings();

  const [micLevel, setMicLevel] = useState(0);
  const [testStream, setTestStream] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [assetUrls, setAssetUrls] = useState({});
  const [isTestingMic, setIsTestingMic] = useState(false);
  const [isTestingSpeaker, setIsTestingSpeaker] = useState(false);

  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cleanup streams on unmount or modal close
  useEffect(() => {
    if (!isModalOpen) {
      stopAllStreams();
    }
    return () => stopAllStreams();
  }, [isModalOpen]);

  const stopAllStreams = useCallback(() => {
    if (testStream) {
      testStream.getTracks().forEach(t => t.stop());
      setTestStream(null);
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setMicLevel(0);
    setIsTestingMic(false);
  }, [testStream, cameraStream]);

  // Load signed URLs for user assets
  useEffect(() => {
    const loadUrls = async () => {
      const urls = {};
      for (const asset of userAssets) {
        const url = await getAssetUrl(asset.storage_path);
        if (url) urls[asset.id] = url;
      }
      setAssetUrls(urls);
    };
    if (userAssets.length > 0) loadUrls();
  }, [userAssets, getAssetUrl]);

  // Start microphone test
  const startMicTest = async () => {
    try {
      const constraints = { audio: settings.selectedMicId ? { deviceId: { exact: settings.selectedMicId } } : true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setTestStream(stream);
      setIsTestingMic(true);

      // Set up audio analysis
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setMicLevel(Math.min(100, avg * 1.5));
        animationRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (err) {
      console.error('[MediaSettings] Mic test failed:', err);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopMicTest = () => {
    if (testStream) {
      testStream.getTracks().forEach(t => t.stop());
      setTestStream(null);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setMicLevel(0);
    setIsTestingMic(false);
  };

  // Test speaker
  const testSpeaker = async () => {
    setIsTestingSpeaker(true);
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        ctx.close();
        setIsTestingSpeaker(false);
      }, 1000);
    } catch (err) {
      console.error('[MediaSettings] Speaker test failed:', err);
      setIsTestingSpeaker(false);
    }
  };

  // Start camera preview
  const startCameraPreview = async () => {
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop());
      }
      const constraints = {
        video: settings.selectedCameraId
          ? { deviceId: { exact: settings.selectedCameraId }, width: { ideal: 640 }, height: { ideal: 480 } }
          : { width: { ideal: 640 }, height: { ideal: 480 } }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('[MediaSettings] Camera preview failed:', err);
    }
  };

  // Auto-start camera preview when on camera/background tab
  useEffect(() => {
    if (isModalOpen && (activeTab === 'camera' || activeTab === 'background')) {
      startCameraPreview();
    } else if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
  }, [isModalOpen, activeTab, settings.selectedCameraId]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const asset = await uploadBackgroundImage(file);
    if (asset) {
      saveSettings({ vbAssetId: asset.id, vbMode: 'image', vbPresetId: null });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (!isModalOpen) return null;

  return (
    <div className="media-settings-overlay" onClick={closeMediaSettings}>
      <div className="media-settings-modal" onClick={e => e.stopPropagation()}>
        <div className="media-settings-header">
          <h2>Media Settings</h2>
          <button className="media-settings-close" onClick={closeMediaSettings}>×</button>
        </div>

        <div className="media-settings-body">
          {/* Tabs */}
          <div className="media-settings-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`media-settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="media-settings-content">
            {/* Microphone Tab */}
            {activeTab === 'microphone' && (
              <div className="settings-section">
                <h3>Microphone</h3>
                <select
                  value={settings.selectedMicId || ''}
                  onChange={e => saveSettings({ selectedMicId: e.target.value || null })}
                >
                  <option value="">Default Microphone</option>
                  {devices.microphones.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone ${d.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>

                <div className="mic-test-section">
                  <div className="mic-level-bar">
                    <div className="mic-level-fill" style={{ width: `${micLevel}%` }} />
                  </div>
                  <div className="test-buttons">
                    {!isTestingMic ? (
                      <button className="test-btn" onClick={startMicTest}>Test Microphone</button>
                    ) : (
                      <button className="test-btn stop" onClick={stopMicTest}>Stop Test</button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Speaker Tab */}
            {activeTab === 'speaker' && (
              <div className="settings-section">
                <h3>Speaker / Output</h3>
                <select
                  value={settings.selectedSpeakerId || ''}
                  onChange={e => saveSettings({ selectedSpeakerId: e.target.value || null })}
                  disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                >
                  <option value="">Default Speaker</option>
                  {devices.speakers.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Speaker ${d.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
                {!('setSinkId' in HTMLMediaElement.prototype) && (
                  <p className="settings-hint">Speaker selection not supported in this browser.</p>
                )}

                <div className="test-buttons">
                  <button 
                    className="test-btn" 
                    onClick={testSpeaker}
                    disabled={isTestingSpeaker}
                  >
                    {isTestingSpeaker ? 'Playing...' : 'Test Speaker'}
                  </button>
                </div>
              </div>
            )}

            {/* Camera Tab */}
            {activeTab === 'camera' && (
              <div className="settings-section">
                <h3>Camera</h3>
                <select
                  value={settings.selectedCameraId || ''}
                  onChange={e => {
                    saveSettings({ selectedCameraId: e.target.value || null });
                  }}
                >
                  <option value="">Default Camera</option>
                  {devices.cameras.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>

                <div className="camera-preview">
                  <video ref={videoRef} autoPlay playsInline muted />
                  {!cameraStream && <div className="camera-placeholder">Camera preview</div>}
                </div>
              </div>
            )}

            {/* Background Tab */}
            {activeTab === 'background' && (
              <div className="settings-section">
                <h3>Virtual Background</h3>

                {/* Mode selector */}
                <div className="vb-mode-selector">
                  <button
                    className={`vb-mode-btn ${settings.vbMode === 'none' ? 'active' : ''}`}
                    onClick={() => saveSettings({ vbMode: 'none', vbPresetId: null, vbAssetId: null })}
                  >
                    None
                  </button>
                  <button
                    className={`vb-mode-btn ${settings.vbMode === 'blur' ? 'active' : ''}`}
                    onClick={() => saveSettings({ vbMode: 'blur', vbPresetId: null, vbAssetId: null })}
                  >
                    Blur
                  </button>
                  <button
                    className={`vb-mode-btn ${settings.vbMode === 'image' ? 'active' : ''}`}
                    onClick={() => saveSettings({ vbMode: 'image' })}
                  >
                    Image
                  </button>
                </div>

                {/* Blur strength slider */}
                {settings.vbMode === 'blur' && (
                  <div className="blur-strength">
                    <label>Blur Strength: {settings.vbBlurStrength}</label>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={settings.vbBlurStrength}
                      onChange={e => saveSettings({ vbBlurStrength: parseInt(e.target.value) })}
                    />
                  </div>
                )}

                {/* Image selection */}
                {settings.vbMode === 'image' && (
                  <>
                    {/* Presets */}
                    {presets.length > 0 && (
                      <div className="vb-section">
                        <h4>Presets</h4>
                        <div className="vb-grid">
                          {presets.map(preset => (
                            <div
                              key={preset.id}
                              className={`vb-item ${settings.vbPresetId === preset.id ? 'selected' : ''}`}
                              onClick={() => saveSettings({ vbPresetId: preset.id, vbAssetId: null })}
                            >
                              <img src={preset.image_url} alt={preset.name} />
                              <span>{preset.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* User uploads */}
                    <div className="vb-section">
                      <h4>Your Images</h4>
                      <div className="vb-grid">
                        {userAssets.map(asset => (
                          <div
                            key={asset.id}
                            className={`vb-item ${settings.vbAssetId === asset.id ? 'selected' : ''}`}
                            onClick={() => saveSettings({ vbAssetId: asset.id, vbPresetId: null })}
                          >
                            {assetUrls[asset.id] ? (
                              <img src={assetUrls[asset.id]} alt={asset.name} />
                            ) : (
                              <div className="vb-loading">Loading...</div>
                            )}
                            <button
                              className="vb-delete"
                              onClick={e => {
                                e.stopPropagation();
                                if (confirm('Delete this background?')) {
                                  deleteBackgroundImage(asset.id);
                                }
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <div
                          className="vb-item vb-upload"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <span className="upload-icon">+</span>
                          <span>Upload</span>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                    </div>
                  </>
                )}

                {/* Camera preview with background */}
                <div className="camera-preview vb-preview">
                  <video ref={videoRef} autoPlay playsInline muted />
                  {!cameraStream && <div className="camera-placeholder">Camera preview</div>}
                  {settings.vbMode !== 'none' && (
                    <div className="vb-preview-badge">
                      {settings.vbMode === 'blur' ? 'Blur preview coming in Milestone B' : 'Background preview coming in Milestone B'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === 'advanced' && (
              <div className="settings-section">
                <h3>Audio Processing</h3>
                <div className="toggle-group">
                  <label className="toggle-item">
                    <input
                      type="checkbox"
                      checked={settings.echoCancellation}
                      onChange={e => saveSettings({ echoCancellation: e.target.checked })}
                    />
                    <span>Echo Cancellation</span>
                  </label>
                  <label className="toggle-item">
                    <input
                      type="checkbox"
                      checked={settings.noiseSuppression}
                      onChange={e => saveSettings({ noiseSuppression: e.target.checked })}
                    />
                    <span>Noise Suppression</span>
                  </label>
                  <label className="toggle-item">
                    <input
                      type="checkbox"
                      checked={settings.autoGainControl}
                      onChange={e => saveSettings({ autoGainControl: e.target.checked })}
                    />
                    <span>Auto Gain Control</span>
                  </label>
                </div>

                <h3>Diagnostics</h3>
                <div className="diagnostics">
                  <p><strong>Selected Mic:</strong> {settings.selectedMicId || 'Default'}</p>
                  <p><strong>Selected Speaker:</strong> {settings.selectedSpeakerId || 'Default'}</p>
                  <p><strong>Selected Camera:</strong> {settings.selectedCameraId || 'Default'}</p>
                  <p><strong>Speaker Selection:</strong> {'setSinkId' in HTMLMediaElement.prototype ? 'Supported' : 'Not supported'}</p>
                </div>

                <button className="reset-btn" onClick={() => {
                  if (confirm('Reset all media settings to defaults?')) {
                    saveSettings({
                      selectedMicId: null,
                      selectedSpeakerId: null,
                      selectedCameraId: null,
                      echoCancellation: true,
                      noiseSuppression: true,
                      autoGainControl: true,
                      vbMode: 'none',
                      vbBlurStrength: 8,
                      vbPresetId: null,
                      vbAssetId: null,
                    });
                  }
                }}>
                  Reset to Defaults
                </button>
              </div>
            )}
          </div>
        </div>

        {loading && <div className="media-settings-loading">Saving...</div>}
      </div>
    </div>
  );
}
